import type { ReactElement } from "react"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { BoxWithMediaCardsSectionFlatData } from "@gatsby-plugin-generic-page/fragments/boxWithMediaCardsSection"
import { Text } from "@ionos-web-design-system/react"
import isEmpty from "~/lib/isEmpty"
import MediaCard from "./MediaCard"

export default function BoxWithMediaCardsSection({
  header,
  featuredCard,
  mediaCards,
  sectionSettings,
}: BoxWithMediaCardsSectionFlatData): ReactElement<BoxWithMediaCardsSectionFlatData> {
  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {!isEmpty(header) ? (
          <div className="mb-7">
            <Header {...header} />
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
          <div className="bg-surface-subtle rounded-sm h-full p-3">
            {featuredCard.responsiveImages ? (
              <Media media={featuredCard.responsiveImages} />
            ) : null}

            <div className="p-3">
              {featuredCard.caption ? (
                <Text
                  variant="bodyMd"
                  color="base"
                  className="uppercase mb-2"
                  asChild
                >
                  <h5>{featuredCard.caption}</h5>
                </Text>
              ) : null}

              <Text variant="heading2xl" color="base" weight="bold" asChild>
                <h3>{featuredCard.title}</h3>
              </Text>

              <ConditionalWrapper
                condition={
                  !isEmpty(featuredCard.description) ||
                  !isEmpty(featuredCard.button)
                }
                wrapper={(children) => (
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center mt-4">
                    {children}
                  </div>
                )}
              >
                <>
                  {!isEmpty(featuredCard.description) ? (
                    <MarkdownText
                      typography={{ bold: "medium4_s", default: "medium3_s" }}
                    >
                      {featuredCard.description}
                    </MarkdownText>
                  ) : null}

                  {!isEmpty(featuredCard.button) ? (
                    <CtaElement ctaElement={featuredCard.button} />
                  ) : null}
                </>
              </ConditionalWrapper>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {mediaCards.map((card, index) => (
              <MediaCard key={`${card.title}-${index}`} card={card} />
            ))}
          </div>
        </div>
      </BaseContainer>
    </BaseSection>
  )
}
